import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Megaphone } from "lucide-react";
import { API_URL, IMAGES, SITE } from "../data/content";
import { useLang } from "../i18n";

const FALLBACK_ANNOUNCEMENTS = [
  { id: "vpk", en: "Now enrolling FREE VPK for 2026–27 — space is limited!", es: "¡Inscripciones abiertas para VPK GRATIS 2026–27 — cupo limitado!", cta_en: "Book a Tour", cta_es: "Reservar Tour", href: "/contact" },
  { id: "camp", en: "Summer Camp registration open — weekly themes, field trips & water fun for ages 4–12", es: "Registro abierto para el Campamento de Verano — temas semanales, excursiones y juegos de agua (4–12 años)", cta_en: "See Programs", cta_es: "Ver Programas", href: "/programs" },
  { id: "svpk", en: "FREE Summer VPK — get your Certificate of Eligibility at elcbrevard.org", es: "VPK de Verano GRATIS — obtén tu Certificado de Elegibilidad en elcbrevard.org", cta_en: "Learn More", cta_es: "Más Info", href: "/programs" },
];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className={`flex items-center rounded-full border-2 border-forest/15 bg-white/80 p-0.5 ${className}`}>
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full text-[11px] font-extrabold tracking-wide transition-all duration-300 ${
            lang === l ? "bg-tangerine text-white shadow" : "text-bark hover:text-tangerine"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { lang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [barVisible, setBarVisible] = useState(true);
  const [announcements, setAnnouncements] = useState(FALLBACK_ANNOUNCEMENTS);
  const [aIdx, setAIdx] = useState(0);
  const navigate = useNavigate();

  const LINKS = [
    { to: "/", label: t("nav_home"), testId: "nav-home-link" },
    { to: "/programs", label: t("nav_programs"), testId: "nav-programs-link" },
    { to: "/parent-portal", label: t("nav_portal"), testId: "nav-parent-portal-link" },
    { to: "/blog", label: t("nav_blog"), testId: "nav-blog-link" },
    { to: "/contact", label: t("nav_contact"), testId: "nav-contact-link" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/announcements`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setAnnouncements(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (announcements.length < 2) return undefined;
    const iv = setInterval(() => setAIdx((i) => (i + 1) % announcements.length), 6000);
    return () => clearInterval(iv);
  }, [announcements.length]);

  const cur = announcements[aIdx % announcements.length];

  return (
    <header className="fixed top-0 inset-x-0 z-40">
      <AnimatePresence initial={false}>
        {barVisible && (
          <motion.div
            data-testid="announcement-bar"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="bg-forest text-cream overflow-hidden relative"
          >
            <div className="max-w-7xl mx-auto px-8 sm:px-12 py-2 flex items-center justify-center gap-2.5">
              <Megaphone className="w-3.5 h-3.5 text-amber shrink-0 hidden sm:block" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={`${aIdx}-${lang}`}
                  data-testid="announcement-text"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="text-xs sm:text-sm font-semibold text-center"
                >
                  {lang === "es" ? cur.es : cur.en}
                </motion.span>
              </AnimatePresence>
              {cur.href && (
                <button
                  data-testid="announcement-cta"
                  onClick={() => navigate(cur.href)}
                  className="text-[11px] sm:text-xs font-extrabold text-amber underline underline-offset-2 hover:text-white transition-colors shrink-0"
                >
                  {lang === "es" ? cur.cta_es : cur.cta_en}
                </button>
              )}
            </div>
            <button
              data-testid="announcement-dismiss-btn"
              onClick={() => setBarVisible(false)}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Dismiss announcement"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`transition-all duration-500 ${
        scrolled || open ? "backdrop-blur-xl bg-cream/85 shadow-[0_8px_30px_rgba(28,61,110,0.10)]" : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
              <img
                src={IMAGES.logo}
                alt="The Learning Tree Academy logo"
                className="w-11 h-11 rounded-full object-cover ring-2 ring-tree/30 group-hover:ring-amber transition-all duration-300"
              />
              <span className="font-display font-extrabold text-forest leading-tight text-sm sm:text-base">
                The Learning Tree
                <span className="block text-[11px] font-body font-semibold tracking-[0.22em] uppercase text-tangerine">
                  Academy · Palm Bay
                </span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testId}
                  className={({ isActive }) =>
                    `relative text-sm font-semibold transition-colors duration-300 hover:text-tangerine ${
                      isActive ? "text-tree" : "text-charcoal/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <LangToggle />
              <a
                href={SITE.phoneHref}
                data-testid="nav-call-link"
                className="flex items-center gap-2 text-sm font-bold text-forest hover:text-tangerine transition-colors"
              >
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
              <button
                data-testid="nav-book-tour-cta"
                onClick={() => navigate("/contact")}
                className="rounded-full bg-tangerine text-white px-6 py-2.5 text-sm font-bold hover:bg-clay hover:scale-[1.03] active:scale-95 transition-all duration-300 shadow-lg shadow-tangerine/30"
              >
                {t("nav_book")}
              </button>
            </div>

            <div className="lg:hidden flex items-center gap-3">
              <LangToggle />
              <button
                data-testid="nav-mobile-menu-btn"
                onClick={() => setOpen(!open)}
                className="p-2 text-forest"
                aria-label="Toggle menu"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden overflow-hidden border-t border-bone"
            >
              <div className="px-6 py-6 flex flex-col gap-4">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-${l.testId}`}
                    onClick={() => setOpen(false)}
                    className="text-lg font-display font-bold text-forest"
                  >
                    {l.label}
                  </NavLink>
                ))}
                <button
                  data-testid="mobile-nav-book-tour-cta"
                  onClick={() => { setOpen(false); navigate("/contact"); }}
                  className="mt-2 rounded-full bg-tangerine text-white px-6 py-3 font-bold"
                >
                  {t("nav_book")}
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
